/**
 * usePathDirtyStatus — convenience hook that returns the dirty state of one
 * Voluum path plus a callback to revert it back to the live values. Reads
 * from the shared StandardDraftContext so any row can opt in without prop
 * drilling.
 *
 * Returns `null` outside the StandardDraftProvider so legacy callers (or the
 * Binom side) keep working without changes.
 */

import { useCallback, useMemo } from 'react';

import {
  getPathDirtyFields,
  restorePath,
  type PathDirtyField,
} from './dirty-helpers';
import { useOptionalStandardDraftContext } from './StandardDraftContext';

export interface UsePathDirtyStatusResult {
  dirty: boolean;
  fields: ReadonlyArray<PathDirtyField>;
  restore: () => void;
}

export function usePathDirtyStatus(
  pathId: string | undefined,
): UsePathDirtyStatusResult | null {
  const ctx = useOptionalStandardDraftContext();
  const live = ctx?.flowQuery.data;
  const draft = ctx?.draftState.draft ?? null;
  const patch = ctx?.draftState.patch;

  const restore = useCallback(() => {
    if (!pathId || !live || !patch) return;
    patch((d) => restorePath(d, live, pathId));
  }, [pathId, live, patch]);

  return useMemo<UsePathDirtyStatusResult | null>(() => {
    if (!ctx) return null;
    const status = getPathDirtyFields(live, draft, pathId);
    return {
      dirty: status.dirty,
      fields: Array.from(status.fields),
      restore,
    };
  }, [ctx, live, draft, pathId, restore]);
}
